/**
 * FAQ Review Panel
 * Shared FAQ review UI used both inside Semantic Schema (HubSpot, full schema/injection
 * controls) and standalone on the Post FAQs page (HTML/no-CMS clients, review-only —
 * there is no injection target so the per-post schema toggle is hidden).
 */

import { useState, useEffect, useCallback } from 'react';
import {
  HelpCircle,
  Loader2,
  RefreshCw,
  Search,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { schemaApi, type FaqGroup } from '../../lib/dashboard-api';

interface FaqReviewPanelProps {
  siteId: number;
  /** Show the per-post "SCHEMA: ON/OFF" toggle and injection-related copy. Only relevant
   *  for CMS clients where FAQs can actually be injected into the page (HubSpot). */
  showSchemaToggle?: boolean;
}

export function FaqReviewPanel({ siteId, showSchemaToggle = true }: FaqReviewPanelProps) {
  const [groups, setGroups]     = useState<FaqGroup[]>([]);
  const [loading, setLoading]   = useState(true);
  const [fetching, setFetching] = useState(false);
  const [fetchMsg, setFetchMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [toggling, setToggling] = useState<Set<string>>(new Set());
  const [search, setSearch]     = useState('');

  const loadFaqs = useCallback(() => {
    setLoading(true);
    schemaApi.listFaqs(siteId)
      .then(result => setGroups(result.faqs))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [siteId]);

  useEffect(() => { loadFaqs(); }, [loadFaqs]);

  const handleFetchFromBka = async () => {
    setFetching(true);
    setFetchMsg(null);
    try {
      const result = await schemaApi.fetchFaqsFromBka(siteId);
      setFetchMsg(`Synced ${result.totalStored} FAQs across ${result.postCount} posts.`);
      loadFaqs();
    } catch {
      setFetchMsg('Failed to fetch FAQs. Check logs.');
    } finally {
      setFetching(false);
    }
  };

  const handlePostToggle = async (postId: string, enabled: boolean) => {
    setToggling(t => new Set(t).add(postId));
    // Optimistic update
    setGroups(gs => gs.map(g => g.postId === postId ? { ...g, faqsEnabled: enabled } : g));
    try {
      await schemaApi.togglePostFaqs(siteId, postId, enabled);
    } catch {
      // Revert on error
      setGroups(gs => gs.map(g => g.postId === postId ? { ...g, faqsEnabled: !enabled } : g));
    } finally {
      setToggling(t => { const n = new Set(t); n.delete(postId); return n; });
    }
  };

  const confidencePct = (c: number | null) =>
    c != null ? `${Math.round(c * 100)}%` : null;

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;
  }

  const filteredGroups = search.trim()
    ? groups.filter(g =>
        (g.postTitle ?? `Post ${g.postId}`)
          .toLowerCase()
          .includes(search.trim().toLowerCase())
      )
    : groups;

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Toolbar row 1: description + fetch button */}
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-white/60">
          {showSchemaToggle
            ? 'Toggle FAQs on or off per post. When enabled, all FAQs for that post are included in its schema output and FAQ section injection (if enabled).'
            : 'FAQs are automatically extracted from each post during analysis. Review the questions and answers generated for every post below.'}
        </p>
        <button
          onClick={handleFetchFromBka}
          disabled={fetching}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-tertiary)] hover:bg-[var(--bg-tertiary)]/80 text-xs font-medium text-white/80 disabled:opacity-50 transition-colors shrink-0"
        >
          {fetching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {fetching ? 'Syncing…' : 'Fetch FAQs'}
        </button>
      </div>

      {/* Toolbar row 2: search */}
      {groups.length > 0 && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search posts by title…"
            className="w-full pl-8 pr-8 py-2 bg-[var(--bg-tertiary)] border border-white/10 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#ffdc18]/40"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {fetchMsg && (
        <p className={`text-xs ${fetchMsg.startsWith('Failed') ? 'text-red-400' : 'text-green-400'}`}>
          {fetchMsg}
        </p>
      )}

      {groups.length === 0 ? (
        <div className="text-center py-16 text-white/40">
          <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p>No FAQ data found.</p>
          <p className="text-xs mt-1">
            {showSchemaToggle
              ? 'Run "Fetch FAQs" above, or enable FAQ extraction and run a baseline analysis.'
              : 'Run "Fetch FAQs" above, or run a new analysis to generate FAQs for your posts.'}
          </p>
        </div>
      ) : filteredGroups.length === 0 ? (
        <div className="text-center py-10 text-white/40">
          <Search className="w-6 h-6 mx-auto mb-2 opacity-40" />
          <p className="text-sm">No posts match "<span className="text-white/60">{search}</span>"</p>
          <button onClick={() => setSearch('')} className="mt-2 text-xs text-[#ffdc18]/60 hover:text-[#ffdc18]">Clear search</button>
        </div>
      ) : filteredGroups.map(group => {
        const isOpen  = expanded.has(group.postId);
        const isTogglingPost = toggling.has(group.postId);
        const title   = group.postTitle || `Post ${group.postId}`;
        return (
          <div key={group.postId} className="bg-[var(--bg-secondary)] rounded-xl border border-[var(--bg-tertiary)] overflow-hidden">
            {/* Post header row */}
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Expand toggle */}
              <button
                onClick={() => setExpanded(s => { const n = new Set(s); isOpen ? n.delete(group.postId) : n.add(group.postId); return n; })}
                className="flex items-center gap-2 flex-1 text-left min-w-0"
              >
                <FileText className="w-4 h-4 text-white/40 shrink-0" />
                <span className="text-sm font-medium truncate">{title}</span>
                <span className="text-xs text-white/40 shrink-0">{group.faqs.length} FAQ{group.faqs.length !== 1 ? 's' : ''}</span>
                {isOpen
                  ? <ChevronUp className="w-4 h-4 text-white/40 shrink-0 ml-auto" />
                  : <ChevronDown className="w-4 h-4 text-white/40 shrink-0 ml-auto" />
                }
              </button>

              {/* View Post link */}
              {group.postUrl && (
                <a
                  href={group.postUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-[#ffdc18]/70 hover:text-[#ffdc18] transition-colors shrink-0"
                  onClick={e => e.stopPropagation()}
                >
                  View Post ↗
                </a>
              )}

              {/* SCHEMA ON/OFF toggle — only meaningful where schema/FAQ injection exists */}
              {showSchemaToggle && (
                <div className="flex items-center gap-2 shrink-0">
                  <span className={`text-xs font-semibold ${group.faqsEnabled ? 'text-[#ffdc18]' : 'text-white/30'}`}>
                    SCHEMA: {group.faqsEnabled ? 'ON' : 'OFF'}
                  </span>
                  <button
                    disabled={isTogglingPost}
                    onClick={() => handlePostToggle(group.postId, !group.faqsEnabled)}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
                      group.faqsEnabled ? 'bg-[#ffdc18]' : 'bg-white/20'
                    }`}
                    aria-label={group.faqsEnabled ? 'Disable FAQs for this post' : 'Enable FAQs for this post'}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                      group.faqsEnabled ? 'translate-x-4' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
              )}
            </div>

            {/* Expanded FAQ list — read-only */}
            {isOpen && (
              <div className="border-t border-[var(--bg-tertiary)]">
                {group.faqs.map((faq, idx) => (
                  <div
                    key={faq.id}
                    className={`px-4 py-4 ${idx < group.faqs.length - 1 ? 'border-b border-[var(--bg-tertiary)]/50' : ''}`}
                  >
                    {/* Question row */}
                    <div className="flex items-start justify-between gap-3 mb-1.5">
                      <p className="text-sm font-medium leading-snug">{faq.question}</p>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {faq.confidence != null && (
                          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                            faq.confidence >= 0.9 ? 'bg-green-500/20 text-green-400'
                            : faq.confidence >= 0.7 ? 'bg-[#ffdc18]/20 text-[#ffdc18]'
                            : 'bg-white/10 text-white/40'
                          }`}>
                            {confidencePct(faq.confidence)}
                          </span>
                        )}
                        {faq.questionType && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-white/50 uppercase tracking-wide">
                            {faq.questionType}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Answer */}
                    <p className="text-sm text-white/60 leading-relaxed">{faq.answer}</p>
                    {/* Related concepts */}
                    {faq.relatedConcepts && faq.relatedConcepts.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {faq.relatedConcepts.map(c => (
                          <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[var(--bg-tertiary)] text-white/35">{c}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
